import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { gql, useQuery, useMutation } from "@apollo/client";
import {
  Box,
  useTheme,
  Grid,
  Typography,
  Button,
  Paper,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Badge,
  Card,
  useMediaQuery,
} from "@mui/material";
import LoadingPage from "./LoadingPage";
import ErrorPage from "./ErrorPage";
import Preloader from "../Components/Preloader/Preloader";
import { useGetMe } from "../Context/GetMeContext";
import { useKitchenDetail } from "../Context/GetMyKitchenDetailContext";
import { useNotification } from "../Context/notificationContext";
import IngredientsPanel from "../Components/CookRecipe/IngredientsPanel";
import RecipeDetailsPanel from "../Components/CookRecipe/RecipeDetailsPanel";
import PreviousRecipeCard from "../Components/CookRecipe/PreviousRecipeCard";
import CookingControls from "../Components/CookRecipe/CookingControls";
import RecipeCustomizer from "../Components/RecipeCustomization/RecipeCustomizer";
import { ingredientTasksMap, validateTaskType } from "../constants";
import { mergeRecipes } from "../Validation/MergeTasks";
import { generateCode } from "../Static/code-generator";
import { validateTask } from "../Validation/TaskValidation";
import { tasks as staticTasks } from "../Static/task";
import CookLaterDialog from "../Components/Dialogs/CookLaterDialog";
import BreadCrumb from "../Components/BreadCrumb";
import {
  BugReport,
  Close,
  Code,
  FoodBank,
  FreeBreakfast,
  Kitchen,
  LocalDrink,
  Opacity,
  Restaurant,
  Spa,
  Tune as CustomizeIcon,
} from "@mui/icons-material";
import DebugPanel from "../Components/Debug/DebugPanel";
import { cookingTasksValidation } from "../Validation/cookingTasksValidation";
import {
  SpaIcon,
  RestaurantIcon,
  FreeBreakfastIcon,
  OpacityIcon,
  KitchenIcon,
  LocalDrinkIcon,
  FoodBankIcon,
} from "@mui/icons-material";

const GET_BOXES = gql`
  query GetBoxes(
    $kitchen_id: ID!
    $user_id: ID!
    $inventory_type: InventoryType!
  ) {
    getBoxes(
      kitchen_id: $kitchen_id
      user_id: $user_id
      inventory_type: $inventory_type
    ) {
      id
      inventory_type
      box_no
      item_name
      box_capacity
      box_filled
      box_unit
      created
      modified
    }
  }
`;
const GET_RECIPE_ACTIONS_QUERY = gql`
  query GetRecipeActions($recipeId: ID!) {
    getRecipeActions(id: $recipeId) {
      id
      recipe_tasks
      recipe_name
      recipe_type
      recipe_category
      recipe_cooking_duration
      serving
      cook_time
      prep_time
      recipe_image
    }
  }
`;

const ADD_COOKING_HISTORY_MUTATION = gql`
  mutation AddCookingHistory(
    $recipe_id: [ID!]
    $kitchen_id: ID!
    $user_id: ID!
    $cooking_status: CookingStatus!
    $cooking_tasks: String
    $cook_in: [String]
    $recipe_name: [String]
  ) {
    addCookingHistory(
      recipe_id: $recipe_id
      kitchen_id: $kitchen_id
      user_id: $user_id
      cooking_status: $cooking_status
      cooking_tasks: $cooking_tasks
      cook_in: $cook_in
      recipe_name: $recipe_name
    ) {
      id
      cooking_status
    }
  }
`;

const UPDATE_INVENTORY_BOX = gql`
  mutation RefillInventory($id: ID!, $input: InventoryInput!) {
    refillInventory(id: $id, input: $input) {
      id
      inventory_type
      box_no
      item_name
      box_capacity
      box_filled
      box_unit
    }
  }
`;

const mobileResponsiveStyles = {
  maxWidth: "100vw",
  overflowX: "hidden",
  boxSizing: "border-box",
  padding: "8px",
  "@media (max-width: 600px)": {
    padding: "4px",
  },
};

const getContainerTypeInfo = (theme) => ({
  spice: {
    icon: <Spa />,
    name: "Spice Box",
    color: theme.palette.error.main,
    description: "For spices and seasonings",
  },
  grain: {
    icon: <Restaurant />,
    name: "Grain Container",
    color: theme.palette.warning.main,
    description: "For rice, pasta, lentils",
  },
  paste: {
    icon: <FreeBreakfast />,
    name: "Sauce Bottle",
    color: theme.palette.grey[600],
    description: "For sauces and pastes",
  },
  oil: {
    icon: <Opacity />,
    name: "Oil Bottle",
    color: theme.palette.warning.light,
    description: "For oils and liquid ingredients",
  },
  bowl: {
    icon: <Kitchen />,
    name: "Bowl",
    color: theme.palette.info.main,
    description: "For mixing ingredients",
  },
  cup: {
    icon: <LocalDrink />,
    name: "Cup",
    color: theme.palette.success.main,
    description: "For measured ingredients",
  },
  soak: {
    icon: <FoodBank />,
    name: "Soaking Bowl",
    color: theme.palette.secondary.main,
    description: "For soaking ingredients",
  },
});

const CookRecipeNew = React.memo(() => {
  const theme = useTheme();
  const { recipe_id } = useParams();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { user } = useGetMe();
  const { kitchenDetail = {} } = useKitchenDetail() || {};
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [showMergeConfirm, setShowMergeConfirm] = useState(false);
  const [mergeInfo, setMergeInfo] = useState({
    prevRecipeName: "",
    currentRecipeName: "",
    prevNutriPot: "",
    currentNutriPot: "",
  });

  // State management
  const [tasks, setTasks] = useState([]);
  const [stationSelected, setStationSelected] = useState(
    localStorage.getItem("stationSelected") || "Pot1"
  );
  const [isReadyToCook, setIsReadyToCook] = useState(false);
  const [preloader, setPreloader] = useState(true);
  const [openScheduleModal, setOpenScheduleModal] = useState(false);
  const [scheduledDate, setScheduledDate] = useState(null);
  const [servingCount, setServingCount] = useState(
    parseInt(localStorage.getItem("currentServingCount")) || 4
  );
  const [prevKitchenTask, setPrevKitchenTask] = useState(null);
  const [currentCookingHistoryId, setCurrentCookingHistoryId] = useState(null);
  const [isViewingPrevRecipe, setIsViewingPrevRecipe] = useState(true);

  const [searchParams] = useSearchParams();
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const [showRecipeCustomizer, setShowRecipeCustomizer] = useState(false);
  const [originalQuantities, setOriginalQuantities] = useState({});
  const debugMode = searchParams.get("debug") === "true";
  const prevStationRef = useRef(stationSelected);
  const prevRecipeIdRef = useRef(recipe_id);
  const taskUpdateTimeoutRef = useRef(null);
  const [mappedPositions, setMappedPositions] = useState({});

  // Load previous kitchen task data with more safety checks
  useEffect(() => {
    try {
      const storedTask = localStorage.getItem("kitchenTask");
      if (storedTask) {
        const parsedTask = JSON.parse(storedTask);
        // Validate that the stored task has the necessary structure
        if (
          parsedTask &&
          parsedTask.recipe &&
          parsedTask.tasks &&
          Array.isArray(parsedTask.tasks)
        ) {
          setPrevKitchenTask(parsedTask);
        } else {
          // Clean up invalid data
          console.warn(
            "Invalid kitchen task data found in localStorage, removing it"
          );
          // localStorage.removeItem("kitchenTask");
          setPrevKitchenTask(null);
        }
      } else {
        setPrevKitchenTask(null);
      }
    } catch (error) {
      console.error("Error loading previous kitchen task:", error);
      // localStorage.removeItem("kitchenTask");
      setPrevKitchenTask(null);
    }
  }, []);

  // Mutations
  const [addCookingHistory] = useMutation(ADD_COOKING_HISTORY_MUTATION);
  const [updateInventoryBox, { loading: updatingInventory }] = useMutation(
    UPDATE_INVENTORY_BOX,
    {
      // This ensures the cache is immediately updated after mutation
      update(cache, { data: { refillInventory } }) {
        if (refillInventory) {
          // Update the cache for this specific inventory box
          cache.modify({
            fields: {
              getBoxes(existingBoxes = []) {
                // Find and replace the updated box in the cache
                return existingBoxes.map((box) =>
                  box.__ref === `Inventory:${refillInventory.id}`
                    ? cache.writeFragment({
                        data: refillInventory,
                        fragment: gql`
                          fragment UpdatedBox on Inventory {
                            id
                            inventory_type
                            box_no
                            item_name
                            box_capacity
                            box_filled
                            box_unit
                          }
                        `,
                      })
                    : box
                );
              },
            },
          });
        }
      },
      // This ensures we always get fresh data after a mutation
      refetchQueries: [
        {
          query: GET_BOXES,
          variables: {
            kitchen_id: kitchenDetail?.kitchenId,
            user_id: user?.me?.id,
            inventory_type: "ALL",
          },
        },
      ],
    }
  );

  // Data fetching
  const {
    data: getBoxesData,
    loading: boxesLoading,
    refetch: refetchInventory,
  } = useQuery(GET_BOXES, {
    variables: {
      kitchen_id: kitchenDetail?.kitchenId,
      user_id: user?.me?.id,
      inventory_type: "ALL",
    },
    skip: !kitchenDetail?.kitchenId || !user?.me?.id,
    fetchPolicy: "network-only", // Always get fresh data from server
    nextFetchPolicy: "network-only", // Keep getting fresh data on refetch
    notifyOnNetworkStatusChange: true, // Important for tracking loading state
  });

  const { data, loading, error } = useQuery(GET_RECIPE_ACTIONS_QUERY, {
    variables: { recipeId: recipe_id },
    skip: !recipe_id,
  });

  // Reset state when recipe_id changes
  useEffect(() => {
    if (recipe_id !== prevRecipeIdRef.current) {
      // Clear any pending task updates
      if (taskUpdateTimeoutRef.current) {
        clearTimeout(taskUpdateTimeoutRef.current);
      }

      // Clear previous recipe data
      setTasks([]);
      setIsReadyToCook(false);
      prevRecipeIdRef.current = recipe_id;
    }
  }, [recipe_id]);

  // Memoized values
  const inventoryData = useMemo(() => getBoxesData?.getBoxes, [getBoxesData]);
  const recipe = data?.getRecipeActions;

  // Add a unit conversion helper at the top of your component (before the main component function)
  const convertToStandardUnit = (quantity, fromUnit, toUnit) => {
    // Convert everything to ml or g for comparison
    const conversionFactors = {
      // Volume conversions
      tbsp: 15, // 1 tablespoon = 15 ml
      "tea spoon": 5, // 1 teaspoon = 5 ml
      cup: 240, // 1 cup = 240 ml
      ml: 1, // base unit
      l: 1000, // 1 liter = 1000 ml

      // Weight conversions
      g: 1, // base unit
      kg: 1000, // 1 kg = 1000 g
      oz: 28.35, // 1 oz = 28.35 g
      lb: 453.59, // 1 lb = 453.59 g
    };

    // Normalize units by removing plurals and lowercase
    const normalizeUnit = (unit) => {
      if (!unit) return "";
      const normalized = unit.toLowerCase().trim();
      if (normalized.endsWith("s") && normalized !== "oz") {
        return normalized.slice(0, -1);
      }
      return normalized;
    };

    const normalizedFromUnit = normalizeUnit(fromUnit);
    const normalizedToUnit = normalizeUnit(toUnit);

    // If units are the same or undefined, return original quantity
    if (
      !normalizedFromUnit ||
      !normalizedToUnit ||
      normalizedFromUnit === normalizedToUnit
    ) {
      return quantity;
    }

    // Convert to standard unit (ml or g)
    if (
      conversionFactors[normalizedFromUnit] &&
      conversionFactors[normalizedToUnit]
    ) {
      return (
        (quantity * conversionFactors[normalizedFromUnit]) /
        conversionFactors[normalizedToUnit]
      );
    }

    // If no conversion possible, return original quantity
    console.warn(`No conversion available from ${fromUnit} to ${toUnit}`);
    return quantity;
  };

  const checkIfReadyToCook = useCallback((currentTasks) => {
    // Skip the check if there are no tasks
    if (!currentTasks || currentTasks.length === 0) {
      setIsReadyToCook(false);
      return;
    }

    // Check if there are any non-ingredient tasks
    const hasNonIngredientTask = currentTasks.some(
      (t) => !ingredientTasksMap[t.name]
    );

    // If any non-ingredient tasks exist, we're ready to cook
    if (hasNonIngredientTask) {
      console.log("Non-ingredient tasks found, ready to cook");
      setIsReadyToCook(true);
      return;
    }

    // If we only have ingredient tasks, check if they're all OK
    const ingredientTasks = currentTasks.filter(
      (t) => ingredientTasksMap[t.name]
    );

    // If no ingredient tasks, we're ready to cook
    if (ingredientTasks.length === 0) {
      setIsReadyToCook(true);
      return;
    }

    // Check if all ingredient tasks are OK and have valid cookIn values
    const allTasksOk = ingredientTasks.every(
      (t) => t.isOk && ["np1", "np2"].includes(t.cookIn)
    );

    console.log(
      `Ingredient tasks check: ${allTasksOk ? "Ready to cook" : "Not ready"}`
    );
    setIsReadyToCook(allTasksOk);
  }, []);

  // Define matchInventoryWithTasks early, before it's used by other functions
  const matchInventoryWithTasks = useCallback(
    (fetchedTasks) => {
      if (!inventoryData?.length || !fetchedTasks?.length) {
        console.log("fetchedTasks", fetchedTasks);
        checkIfReadyToCook(fetchedTasks);
        return fetchedTasks;
      }

      // Force a fresh check against inventory for every task
      const updatedTasks = fetchedTasks.map((task) => {
        // CRITICAL: Don't override manually assigned positions or types
        // If the task already has a manually set position (marked by isOk=true), preserve it
        if (task.position && task.isOk === true) {
          // Special handling for non-inventory types like cups and bowls
          // These types don't need inventory checks once assigned
          const nonInventoryTypes = ["choose_cup", "choose_bowl"];
          if (nonInventoryTypes.includes(task.type)) {
            return {
              ...task,
              errorMessage: null,
              canBeRefilled: false,
              isOk: true,
            };
          }

          // For all other types, check the inventory status
          const taskType = validateTaskType(task.type);
          const inventoryItem = inventoryData.find(
            (inv) =>
              inv.item_name === task.item_name &&
              taskType?.toLowerCase() === inv.inventory_type?.toLowerCase() &&
              String(inv.box_no) === String(task.position)
          );

          // Check if inventory item exists and if inventory level is sufficient
          if (inventoryItem) {
            // IMPORTANT: Get real-time inventory levels for proper status update
            // Convert units if necessary
            const requiredQuantity = parseFloat(task.quantity);
            const availableQuantity = parseFloat(inventoryItem.box_filled);

            // Convert task quantity to inventory unit if units don't match
            let requiredQuantityConverted = requiredQuantity;
            if (
              task.unit &&
              inventoryItem.box_unit &&
              task.unit !== inventoryItem.box_unit
            ) {
              requiredQuantityConverted = convertToStandardUnit(
                requiredQuantity,
                task.unit,
                inventoryItem.box_unit
              );
              console.log(
                `Converting ${requiredQuantity} ${task.unit} to ${requiredQuantityConverted} ${inventoryItem.box_unit} for ${task.item_name}`
              );
            }

            const isInventorySufficient =
              availableQuantity >= requiredQuantityConverted;

            console.log(
              `Inventory check for ${task.item_name}: filled=${availableQuantity}${inventoryItem.box_unit}, needed=${requiredQuantityConverted}${inventoryItem.box_unit}, sufficient=${isInventorySufficient}`
            );

            return {
              ...task,
              errorMessage: !isInventorySufficient
                ? "Inventory level is low, please refill"
                : null,
              canBeRefilled: !isInventorySufficient,
              isOk: isInventorySufficient, // This is critical - update isOk based on current inventory
            };
          }

          // No matching inventory found - show error but don't allow refill for non-inventory types
          if (
            [
              "choose_grains",
              "choose_spices",
              "choose_paste",
              "choose_oil",
            ].includes(task.type)
          ) {
            return {
              ...task,
              errorMessage: "Position assigned but no matching inventory found",
              canBeRefilled: false,
              isOk: true, // Keep it as OK since it's manually assigned
            };
          } else {
            // For other types just keep the assignment
            // IMPORTANT: Enable this check to ensure readiness status is updated
            checkIfReadyToCook([task]);
            return {
              ...task,
              errorMessage: null,
              canBeRefilled: false,
              isOk: true,
            };
          }
        }

        // Only perform automatic matching for tasks without manual assignments
        const taskType = validateTaskType(task.type);
        const inventoryItem = inventoryData.find(
          (inv) =>
            inv.item_name === task.item_name &&
            taskType?.toLowerCase() === inv.inventory_type?.toLowerCase()
        );

        if (!inventoryItem)
          return {
            ...task,
            isOk: false,
            errorMessage: "Ingredient not found in inventory",
            canBeRefilled: false,
          };

        // Store position as string for consistency
        const position = inventoryItem.box_no
          ? String(inventoryItem.box_no)
          : "";

        // IMPORTANT: Convert units and use proper comparison
        const requiredQuantity = parseFloat(task.quantity);
        const availableQuantity = parseFloat(inventoryItem.box_filled);

        // Convert task quantity to inventory unit if units don't match
        let requiredQuantityConverted = requiredQuantity;
        if (
          task.unit &&
          inventoryItem.box_unit &&
          task.unit !== inventoryItem.box_unit
        ) {
          requiredQuantityConverted = convertToStandardUnit(
            requiredQuantity,
            task.unit,
            inventoryItem.box_unit
          );
          console.log(
            `Converting ${requiredQuantity} ${task.unit} to ${requiredQuantityConverted} ${inventoryItem.box_unit} for ${task.item_name}`
          );
        }

        const isInventorySufficient =
          availableQuantity >= requiredQuantityConverted;

        // Check if we have a saved mapping for this task
        const mappedPosition = mappedPositions[task.id];
        if (mappedPosition) {
          // IMPORTANT: Create a new task object with all original properties
          const updatedTask = { ...task };

          // Use the mapped position
          updatedTask.position = mappedPosition.position;
          updatedTask.isOk = mappedPosition.isOk;

          // If there's a saved type, use that instead of the original
          if (mappedPosition.type && mappedPosition.type !== task.type) {
            // Find template for the saved type
            const taskTemplate = staticTasks?.find(
              (template) => template.type === mappedPosition.type
            );
            if (taskTemplate) {
              updatedTask.type = mappedPosition.type;
              updatedTask.name = taskTemplate.name;
              updatedTask.function = taskTemplate.function;
              updatedTask.category = taskTemplate.category;
              updatedTask.code = taskTemplate.code;
            }
          }

          // Check if this is a non-inventory type that doesn't need refill
          const nonInventoryTypes = ["choose_cup", "choose_bowl"];
          if (nonInventoryTypes.includes(updatedTask.type)) {
            updatedTask.errorMessage = null;
            updatedTask.canBeRefilled = false;
            updatedTask.isOk = true;
            return updatedTask;
          }

          // Even for manually mapped positions, still set the error message if inventory is insufficient
          if (!isInventorySufficient) {
            updatedTask.errorMessage = "Inventory level is low, please refill";
            updatedTask.canBeRefilled = true; // Allow refill for mapped positions
            updatedTask.isOk = false; // IMPORTANT: Set isOk to false when inventory is insufficient
          } else {
            updatedTask.errorMessage = null;
            updatedTask.canBeRefilled = false;
            updatedTask.isOk = true; // Set isOk to true when inventory is sufficient
          }

          return updatedTask;
        }

        // For tasks with no manual mapping, use the automatically detected position
        return {
          ...task,
          position: position,
          isOk: isInventorySufficient,
          errorMessage: !isInventorySufficient
            ? "Inventory level is low, please refill"
            : null,
          canBeRefilled: !isInventorySufficient, // Allow refill for auto-detected positions
        };
      });

      // Now make sure to check if the recipe is ready to cook with updated tasks
      checkIfReadyToCook(updatedTasks);
      return updatedTasks;
    },
    [inventoryData, mappedPositions, checkIfReadyToCook]
  );

  const validateTasks = useCallback((tasksToValidate) => {
    return tasksToValidate?.length
      ? cookingTasksValidation(tasksToValidate)
      : [];
  }, []);

  const handleStationSelect = useCallback(
    (station) => {
      // First, save the current mapped positions
      const positionMap = {};
      tasks.forEach((task) => {
        if (task.position && task.isOk) {
          positionMap[task.id] = {
            position: task.position,
            isOk: task.isOk,
          };
        }
      });

      // Update station in localStorage
      setStationSelected(station);
      localStorage.setItem("stationSelected", station);

      // Update tasks with new station but preserve positions
      setTasks((prevTasks) =>
        prevTasks.map((task) => {
          const savedPosition = positionMap[task.id];
          return {
            ...task,
            cookIn: station,
            // Preserve position and isOk if they exist
            position: savedPosition?.position || task.position,
            isOk:
              savedPosition?.isOk !== undefined
                ? savedPosition.isOk
                : task.isOk,
          };
        })
      );
    },
    [tasks]
  );

  const handleManualEntry = useCallback(
    (taskId, newBoxNo, taskType) => {
      // Ensure newBoxNo is handled properly
      console.log(
        "Manual entry with taskId:",
        taskId,
        "position:",
        newBoxNo,
        "type:",
        taskType
      );
      const sanitizedBoxNo =
        newBoxNo === null || newBoxNo === undefined ? "" : String(newBoxNo); // Convert to string to be consistent

      // Find the current task to get its ingredient name and quantity
      const taskToUpdate = tasks.find((task) => task.id === taskId);
      if (!taskToUpdate) {
        console.error("Task not found:", taskId);
        return;
      }

      // First, update the task in the UI for immediate feedback
      setTasks((prevTasks) => {
        const updatedTasks = prevTasks.map((task) => {
          if (task.id === taskId) {
            // Create updated task with new position
            const updatedTask = {
              ...task,
              position: sanitizedBoxNo,
              isOk: true, // Always mark as OK when manually assigned
              errorMessage: null, // Clear any error messages
              canBeRefilled: false, // No need to refill a newly assigned item
              cookIn: stationSelected,
            };

            // If a specific task type was provided, update the type as well
            if (taskType && taskType !== task.type) {
              console.log(
                `Updating task type from ${task.type} to ${taskType}`
              );

              // Find template for the new task type
              const taskTemplate = staticTasks?.find(
                (t) => t.type === taskType
              );
              if (taskTemplate) {
                // Apply type-specific properties from the template
                updatedTask.type = taskType;
                updatedTask.name = taskTemplate.name;
                updatedTask.duration = taskTemplate.duration;
                updatedTask.function = taskTemplate.function;
                updatedTask.category = taskTemplate.category;
                updatedTask.code = taskTemplate.code;
              }
            }

            return updatedTask;
          }
          return task;
        });

        // Store the mapped position in our mapping state
        setMappedPositions((prev) => ({
          ...prev,
          [taskId]: {
            position: sanitizedBoxNo,
            isOk: true,
            type: taskType, // Store the type in mappedPositions too
          },
        }));

        checkIfReadyToCook(updatedTasks);
        return updatedTasks;
      });

      // Function to handle inventory updates if needed
      const updateInventoryIfNeeded = () => {
        // If this is a manageable inventory type (spice, grain, paste, bottle),
        // and a specific box number is assigned, update the inventory
        const inventoryTypes = [
          "choose_spices",
          "choose_grains",
          "choose_paste",
          "choose_oil",
        ];

        if (inventoryTypes.includes(taskType) && sanitizedBoxNo) {
          // Use the task we already found
          if (!taskToUpdate) return Promise.resolve();

          // Find the inventory box that matches the type and position
          const validatedType = validateTaskType(taskType);
          const boxToUpdate = inventoryData?.find(
            (box) =>
              box.inventory_type?.toLowerCase() ===
                validatedType?.toLowerCase() &&
              String(box.box_no) === sanitizedBoxNo
          );

          if (boxToUpdate) {
            console.log(
              `Updating inventory box ${boxToUpdate.id} with ${taskToUpdate.item_name}`
            );

            // Only update if the box is unlabeled or different from current ingredient
            if (
              boxToUpdate.item_name === "Unlabeled Box" ||
              boxToUpdate.item_name !== taskToUpdate.item_name
            ) {
              // Start visual update to show operation in progress
              showNotification(
                "info",
                `Assigning ${taskToUpdate.item_name} to ${validatedType} ${sanitizedBoxNo}...`,
                2000
              );

              // Update the inventory box with the task's ingredient
              return updateInventoryBox({
                variables: {
                  id: boxToUpdate.id,
                  input: {
                    inventory_type: boxToUpdate.inventory_type,
                    box_no: boxToUpdate.box_no,
                    item_name: taskToUpdate.item_name,
                    box_capacity: parseFloat(boxToUpdate.box_capacity),
                    box_filled: 0, // Set initial quantity to 0 as requested
                    box_unit: boxToUpdate.box_unit,
                    kitchen_id: kitchenDetail?.kitchenId,
                    user_id: user?.me?.id,
                  },
                },
              })
                .then(() => {
                  // After updating inventory, refresh the data
                  showNotification(
                    "success",
                    `${taskToUpdate.item_name} assigned to ${validatedType} ${sanitizedBoxNo}`
                  );

                  // Immediately update tasks to remove error indication
                  setTasks((prevTasks) => {
                    return prevTasks.map((t) => {
                      if (t.id === taskId) {
                        return {
                          ...t,
                          isOk: true,
                          errorMessage: null,
                          canBeRefilled: false,
                        };
                      }
                      return t;
                    });
                  });

                  // Return a promise that resolves after inventory is refetched
                  return new Promise((resolve) => {
                    // Use animation frame for smoother transition
                    requestAnimationFrame(() => {
                      if (
                        refetchInventory &&
                        typeof refetchInventory === "function"
                      ) {
                        refetchInventory()
                          .then(() => {
                            // Force an immediate update of all tasks with fresh inventory data
                            setTasks((prevTasks) =>
                              matchInventoryWithTasks(prevTasks)
                            );
                            resolve();
                          })
                          .catch((err) => {
                            console.error("Error refetching inventory:", err);
                            resolve();
                          });
                      } else {
                        resolve();
                      }
                    });
                  });
                })
                .catch((err) => {
                  console.error("Error updating inventory:", err);
                  showNotification(
                    "error",
                    "Failed to update inventory: " +
                      (err.message || "Unknown error")
                  );
                  return Promise.reject(err);
                });
            }
          }
        }

        // If no inventory update is needed, just mark the task as OK
        setTasks((prevTasks) => {
          return prevTasks.map((t) => {
            if (t.id === taskId) {
              return {
                ...t,
                isOk: true,
                errorMessage: null,
                canBeRefilled: false,
              };
            }
            return t;
          });
        });

        return Promise.resolve();
      };

      // Execute inventory update with proper sequencing
      updateInventoryIfNeeded()
        .then(() => {
          // After everything is complete, do a final check
          checkIfReadyToCook(tasks);
        })
        .catch((err) => {
          console.error("Error in manual entry flow:", err);
        });
    },
    [
      checkIfReadyToCook,
      stationSelected,
      tasks,
      inventoryData,
      updateInventoryBox,
      kitchenDetail?.kitchenId,
      user?.me?.id,
      showNotification,
      refetchInventory,
      matchInventoryWithTasks,
      setMappedPositions,
    ]
  );

  const updateIngredientQuantities = useCallback(
    (newServingCount) => {
      setServingCount(newServingCount);
      if (!tasks || tasks?.length === 0) return;

      const updatedTasks = tasks.map((task) => {
        // Define task types that should NOT be affected by serving size
        const operationBasedTypes = [
          "choose_stir",
          "choose_seconds",
          "heat_on",
          "heat_off",
          "open_lid",
          "close_lid",
          "chimney_on",
          "chimney_off",
          "release_steam",
        ];

        // Calculate the new quantity based on serving proportion only for ingredient tasks
        let newQuantity = task.quantity;
        if (!operationBasedTypes.includes(task.type)) {
          newQuantity = parseFloat(
            ((task.quantity / servingCount) * newServingCount).toFixed(2)
          );
          console.log(
            `🔧 Manual serving adjustment: ${task.name} quantity ${task.quantity} → ${newQuantity} (${servingCount} → ${newServingCount} servings)`
          );
        } else {
          console.log(
            `🔧 Skipping manual serving adjustment for operation: ${task.name} (${task.type})`
          );
        }

        // Preserve position, isOk and other properties for manually mapped tasks
        // Tasks with isOk=true have been manually mapped and should keep their position
        if (task.position && task.isOk === true) {
          return {
            ...task,
            quantity: newQuantity,

            // Keep the existing position and isOk values
          };
        }

        // For unmapped tasks, just update the quantity
        return {
          ...task,
          quantity: newQuantity,
        };
      });

      // Store the serving size in localStorage to make it persistent
      localStorage.setItem("currentServingCount", newServingCount);

      // This will validate the tasks but preserve manually mapped positions
      // because we fixed matchInventoryWithTasks to respect manual mappings
      setTasks((prev) => {
        const matched = matchInventoryWithTasks(updatedTasks);
        return validateTasks(matched);
      });
    },
    [servingCount, tasks, matchInventoryWithTasks, validateTasks]
  );

  // Recipe customization handlers
  const handleCustomizeRecipe = useCallback(() => {
    setShowRecipeCustomizer(true);
  }, []);

  const handleCloseCustomizer = useCallback(() => {
    setShowRecipeCustomizer(false);
  }, []);

  const handleUpdateTasksFromCustomizer = useCallback(
    (updatedTasks, newOriginalQuantities) => {
      console.log("Updating tasks from customizer:", updatedTasks.length);

      // Store/update original quantities if provided
      if (newOriginalQuantities) {
        setOriginalQuantities((prev) => ({
          ...prev,
          ...newOriginalQuantities,
        }));
      }

      // Apply the customized tasks while preserving all existing properties
      setTasks((prevTasks) => {
        const taskMap = new Map(prevTasks.map((task) => [task.id, task]));

        const mergedTasks = updatedTasks.map((updatedTask) => {
          const existingTask = taskMap.get(updatedTask.id);
          if (existingTask) {
            // Merge the updated task with existing properties
            return {
              ...existingTask,
              ...updatedTask,
              // Preserve critical properties that shouldn't be overwritten
              position: existingTask.position,
              isOk: existingTask.isOk,
              errorMessage: existingTask.errorMessage,
              canBeRefilled: existingTask.canBeRefilled,
              cookIn: existingTask.cookIn || stationSelected,
            };
          }
          return updatedTask;
        });

        // Validate the merged tasks
        const validatedTasks = validateTasks(mergedTasks);

        // Re-check inventory matching to ensure everything is still valid
        const finalTasks = matchInventoryWithTasks(validatedTasks);

        showNotification(
          "success",
          "Recipe customizations applied successfully!"
        );
        return finalTasks;
      });
    },
    [stationSelected, validateTasks, matchInventoryWithTasks, showNotification]
  );

  // Memoize data objects
  const recipeDetailsProps = useMemo(
    () => ({
      recipe,
      servingCount,
      onServingChange: updateIngredientQuantities,
      stationSelected,
      onStationChange: handleStationSelect,
      isReady: isReadyToCook,
      prevTask: prevKitchenTask,
      onCustomizeRecipe: handleCustomizeRecipe,
      customizableTasksCount: tasks.filter((t) => t.item_name && t.quantity)
        .length,
    }),
    [
      recipe,
      servingCount,
      stationSelected,
      isReadyToCook,
      prevKitchenTask,
      updateIngredientQuantities,
      handleStationSelect,
      handleCustomizeRecipe,
      tasks,
    ]
  );

  // Effects
  useEffect(() => {
    const timer = setTimeout(() => setPreloader(false), 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (prevStationRef.current !== stationSelected && tasks.length > 0) {
      prevStationRef.current = stationSelected;

      // Update only the cookIn property, preserving all mapped positions
      setTasks((prevTasks) =>
        prevTasks.map((task) => ({
          ...task,
          cookIn: stationSelected,
          // Don't modify position or isOk properties
        }))
      );
    }
  }, [stationSelected, tasks.length]);

  // Enhanced useEffect to handle tasks data with debouncing
  useEffect(() => {
    if (data?.getRecipeActions?.recipe_tasks) {
      console.log(
        "data?.getRecipeActions?.recipe_tasks",
        data?.getRecipeActions
      );
      // Clear any pending task updates
      if (taskUpdateTimeoutRef.current) {
        clearTimeout(taskUpdateTimeoutRef.current);
      }

      // Create a small delay to ensure we don't have race conditions
      taskUpdateTimeoutRef.current = setTimeout(() => {
        try {
          const fetchedTasks = JSON.parse(data.getRecipeActions.recipe_tasks);
          console.log("fetchedTasks", data.getRecipeActions);
          const savedServingCount =
            parseInt(localStorage.getItem("currentServingCount")) ||
            servingCount;
          const originalServingCount = data?.getRecipeActions?.serving || 4;

          // Apply stored mapped positions to new tasks and adjust quantities for current serving size
          const initialTasks = fetchedTasks.map((t) => {
            const mappedPosition = mappedPositions[t.id];

            // Define task types that should NOT be affected by serving size
            const operationBasedTypes = [
              "choose_stir",
              "choose_seconds",
              "heat_on",
              "heat_off",
              "open_lid",
              "close_lid",
              "chimney_on",
              "chimney_off",
              "release_steam",
            ];

            // Apply serving size adjustment only for ingredient-based tasks, not operations
            let adjustedQuantity = t.quantity;
            if (
              savedServingCount !== originalServingCount &&
              !operationBasedTypes.includes(t.type)
            ) {
              adjustedQuantity = parseFloat(
                (
                  (t.quantity / originalServingCount) *
                  savedServingCount
                ).toFixed(2)
              );
              console.log(
                `🔧 Serving size adjustment: ${t.name} quantity ${t.quantity} → ${adjustedQuantity} (${originalServingCount} → ${savedServingCount} servings)`
              );
            } else if (operationBasedTypes.includes(t.type)) {
              console.log(
                `🔧 Skipping serving adjustment for operation: ${t.name} (${t.type})`
              );
            }

            // Start with base task
            const updatedTask = {
              ...t,
              quantity: adjustedQuantity,
              cookIn: stationSelected,
            };

            // If there's a mapped position, use its properties
            if (mappedPosition) {
              updatedTask.position = mappedPosition.position || "";
              updatedTask.isOk =
                mappedPosition.isOk !== undefined
                  ? mappedPosition.isOk
                  : updatedTask.isOk;

              // CRITICAL: If the position has a saved type, use that instead of the original
              if (mappedPosition.type && mappedPosition.type !== t.type) {
                // Don't log every time to reduce console noise
                // console.log(`Preserving mapped type: ${mappedPosition.type} for task ${t.id} (original: ${t.type})`);

                // Find template for the saved type
                const taskTemplate = staticTasks?.find(
                  (template) => template.type === mappedPosition.type
                );
                if (taskTemplate) {
                  // Apply the template properties
                  updatedTask.type = mappedPosition.type;
                  updatedTask.name = taskTemplate.name;
                  updatedTask.function = taskTemplate.function;
                  updatedTask.category = taskTemplate.category;
                  updatedTask.code = taskTemplate.code;
                  // Preserve the other properties like quantity that we set above
                }
              }
            } else {
              // Use original position/isOk if no mapped data
              updatedTask.position = t.position || "";
              updatedTask.isOk = t.isOk;
            }

            return updatedTask;
          });

          const matchedTasks = matchInventoryWithTasks(initialTasks);
          setTasks(matchedTasks);

          // Initialize original quantities on first load
          setOriginalQuantities((prev) => {
            const newOriginals = {};
            let hasNewValues = false;

            initialTasks.forEach((task) => {
              if (!prev[task.id]) {
                // Only set if not already stored
                if (task.item_name && task.quantity) {
                  newOriginals[task.id] = parseFloat(task.quantity);
                  hasNewValues = true;
                } else if (
                  (task.category === "Waiting Action" ||
                    task.type === "choose_seconds") &&
                  task.quantity
                ) {
                  newOriginals[task.id] = parseInt(task.quantity);
                  hasNewValues = true;
                }
              }
            });

            return hasNewValues ? { ...prev, ...newOriginals } : prev;
          });

          // Reduce console logging
          // console.log("Tasks updated from recipe data (preserving mapped positions, types, and serving size)");
        } catch (error) {
          console.error("Error parsing recipe tasks:", error);
          setTasks([]);
        }
      }, 200);

      return () => {
        if (taskUpdateTimeoutRef.current) {
          clearTimeout(taskUpdateTimeoutRef.current);
        }
      };
    }
  }, [
    data,
    matchInventoryWithTasks,
    stationSelected,
    mappedPositions,
    servingCount,
    // REMOVED 'tasks' to prevent infinite loop
    inventoryData, // Add inventoryData to dependencies to refresh when inventory changes
  ]);

  // Keep track of mapped positions whenever tasks change
  useEffect(() => {
    // Skip effect if no tasks to process
    if (!tasks.length) return;

    // Update mappedPositions from current tasks
    const newMappedPositions = {};
    let hasNewPositions = false;

    tasks.forEach((task) => {
      if (task.position && task.isOk) {
        newMappedPositions[task.id] = {
          position: task.position,
          isOk: task.isOk,
          type: task.type, // Save the task type in mappedPositions
        };

        // Check if this is actually a new or changed position
        const existingPos = mappedPositions[task.id];
        if (
          !existingPos ||
          existingPos.position !== task.position ||
          existingPos.type !== task.type ||
          existingPos.isOk !== task.isOk
        ) {
          hasNewPositions = true;
        }
      }
    });

    // Only update state if there are actual changes
    if (hasNewPositions && Object.keys(newMappedPositions).length > 0) {
      setMappedPositions((prev) => {
        // Create merged positions
        const merged = { ...prev };

        // Add/update new positions
        Object.keys(newMappedPositions).forEach((id) => {
          merged[id] = newMappedPositions[id];
        });

        return merged;
      });
    }
  }, [tasks]);

  // Update useEffect to fetch fresh inventory data when needed
  useEffect(() => {
    // When inventory data changes, re-validate inventory matching for tasks
    if (inventoryData?.length > 0 && tasks?.length > 0) {
      setTasks(matchInventoryWithTasks(tasks));
    }
  }, [inventoryData, matchInventoryWithTasks]);

  const handleAddAnotherRecipe = useCallback(() => {
    if (prevKitchenTask) return;

    // Make sure all tasks have the current stationSelected
    const tasksWithStation = tasks.map((task) => ({
      ...task,
      cookIn: stationSelected,
    }));

    // Store comprehensive recipe information
    const kitchenTask = {
      recipe: {
        ...data?.getRecipeActions,
        // Ensure recipe_name is always available
        recipe_name:
          data?.getRecipeActions?.recipe_name ||
          `Recipe ${data?.getRecipeActions?.id}`,
        id: data?.getRecipeActions?.id,
        serving: servingCount,
      },
      tasks: tasksWithStation,
      selectedStation: stationSelected,
      timestamp: new Date().toISOString(), // Add timestamp to track freshness
    };

    localStorage.setItem("kitchenTask", JSON.stringify(kitchenTask));
    navigate("/dashboard");
    showNotification("warning", "Kindly add another recipe...");
  }, [
    data,
    tasks,
    prevKitchenTask,
    navigate,
    showNotification,
    stationSelected,
  ]);

  const removePreKitechenTasks = useCallback(() => {
    if (prevKitchenTask) {
      // Remove from localStorage
      localStorage.removeItem("kitchenTask");

      // Reset state in the component
      setPrevKitchenTask(null);

      // Force a refresh of the current tasks but preserve mapped positions
      if (data?.getRecipeActions?.recipe_tasks) {
        try {
          const fetchedTasks = JSON.parse(data.getRecipeActions.recipe_tasks);

          // Preserve mapped positions if tasks already exist
          if (tasks.length > 0) {
            // Create a map of existing task positions by ID
            const existingPositionsMap = tasks.reduce((map, task) => {
              if (task.position && task.isOk) {
                map[task.id] = {
                  position: task.position,
                  isOk: task.isOk,
                };
              }
              return map;
            }, {});

            // Apply existing positions to new tasks
            const initialTasks = fetchedTasks.map((t) => {
              const existingData = existingPositionsMap[t.id];
              return {
                ...t,
                cookIn: stationSelected,
                position: existingData?.position || t.position || "",
                isOk:
                  existingData?.isOk !== undefined ? existingData.isOk : t.isOk,
              };
            });

            const matchedTasks = matchInventoryWithTasks(initialTasks);
            setTasks(matchedTasks);
          } else {
            const initialTasks = fetchedTasks.map((t) => ({
              ...t,
              cookIn: stationSelected,
            }));
            const matchedTasks = matchInventoryWithTasks(initialTasks);
            setTasks(matchedTasks);
          }
        } catch (error) {
          console.error("Error refreshing tasks after removal:", error);
        }
      }

      showNotification("success", "Previous recipe tasks removed");
    }
  }, [
    prevKitchenTask,
    showNotification,
    data,
    matchInventoryWithTasks,
    stationSelected,
    tasks,
  ]);

  const generateMergedCode = useCallback(
    (currentTasks, prevTasks, currentStation, prevStation) => {
      if (!prevTasks || !prevTasks.tasks || prevTasks.tasks.length === 0) {
        // No previous tasks, just return code for current tasks
        return generateCode(currentTasks);
      }

      // Make sure all previous tasks have their cookIn property set correctly
      const prevTasksWithStation = prevTasks.tasks.map((task) => ({
        ...task,
        cookIn:
          prevTasks.selectedStation || prevStation || task.cookIn || "np1",
      }));

      // Make sure all current tasks have their cookIn property set correctly
      const currentTasksWithStation = currentTasks.map((task) => ({
        ...task,
        cookIn: currentStation || task.cookIn || "np2",
      }));

      // Combine the tasks
      const combinedTasks = [
        ...prevTasksWithStation,
        ...currentTasksWithStation,
      ];

      // Merge the tasks to resolve conflicts
      const mergedTasks = mergeRecipes(combinedTasks);

      console.log("Tasks being merged:", {
        prevTasksWithStation,
        currentTasksWithStation,
        mergedTasks,
      });

      try {
        // Generate code from the merged tasks
        return generateCode(mergedTasks);
      } catch (err) {
        console.error("Merge code generation error:", err);
        return `Error: ${err.message}`;
      }
    },
    []
  );

  const handleCook = useCallback(async () => {
    if (!isReadyToCook) return;

    try {
      // Make sure all tasks have the cookIn property set
      const tasksWithStation = tasks.map((task) => ({
        ...task,
        cookIn: stationSelected,
      }));

      const validatedCurrentTasks = validateTasks(tasksWithStation);

      // Create a cooking history record in the backend
      let cookingHistoryId = null;
      try {
        // Create an array of recipe IDs (current recipe or both recipes if merging)
        const recipeIds = prevKitchenTask
          ? [parseInt(prevKitchenTask.recipe.id), parseInt(recipe_id)]
          : [recipe_id];

        // Create an array of recipe names with fallbacks
        const recipeNames = prevKitchenTask
          ? [
              prevKitchenTask.recipe.recipe_name ||
                `Recipe ${prevKitchenTask.recipe.id || "Previous"}`,
              recipe?.recipe_name || `Recipe ${recipe_id || "Current"}`,
            ]
          : [recipe?.recipe_name || `Recipe ${recipe_id || "Current"}`];

        // Create an array of NutriPots being used
        const cookInValues = prevKitchenTask
          ? [prevKitchenTask.selectedStation || "np1", stationSelected]
          : [stationSelected];

        // Create a cooking history record with PROCESSED status
        const result = await addCookingHistory({
          variables: {
            recipe_id: recipeIds,
            kitchen_id: kitchenDetail.kitchenId,
            user_id: user?.me?.id,
            cooking_status: "PROCESSED",
            cooking_tasks: JSON.stringify({
              tasks: prevKitchenTask
                ? [...prevKitchenTask.tasks, ...validatedCurrentTasks]
                : validatedCurrentTasks,
            }),
            cook_in: cookInValues,
            recipe_name: recipeNames,
          },
        });
        cookingHistoryId = result.data.addCookingHistory.id;
        setCurrentCookingHistoryId(cookingHistoryId);
        console.log("Created cooking history with ID:", cookingHistoryId);
      } catch (error) {
        console.error("Failed to create cooking history:", error);
        // Continue even if cooking history creation fails
      }

      if (prevKitchenTask) {
        // Ensure we have a valid name for previous recipe
        const prevRecipeName =
          prevKitchenTask.recipe?.recipe_name ||
          `Recipe ${prevKitchenTask.recipe?.id || "Previous"}`;

        const currentRecipeName =
          recipe?.recipe_name || `Recipe ${recipe_id || "Current"}`;

        // Get the cookIn from the tasks or from the selectedStation property
        const prevNutriPot =
          (prevKitchenTask.tasks &&
            prevKitchenTask.tasks.length > 0 &&
            prevKitchenTask.tasks[0].cookIn) ||
          prevKitchenTask.selectedStation ||
          "np1";

        // Check if trying to cook in the same NutriPot
        if (prevNutriPot === stationSelected) {
          showNotification(
            "error",
            `Cannot cook both recipes in the same Pot (${
              stationSelected === "np1" ? "Pot 1" : "Pot 2"
            }). Please select a different Pot.`
          );
          return;
        }

        // Set merge information for confirmation dialog
        setMergeInfo({
          prevRecipeName,
          currentRecipeName,
          prevNutriPot,
          currentNutriPot: stationSelected,
        });

        // Show merge confirmation dialog
        setShowMergeConfirm(true);
      } else {
        // No previous kitchen task, generate code for current validated tasks
        const singleCode = generateCode(validatedCurrentTasks);
        navigate(`/cooking-start/${recipe_id}`, {
          state: {
            tasks: validatedCurrentTasks,
            recipe,
            code: singleCode,
            cookingHistoryId: cookingHistoryId,
          },
        });
      }
    } catch (error) {
      console.error("Error in handleCook:", error);
      showNotification("error", error.message);
    }
  }, [
    isReadyToCook,
    tasks,
    prevKitchenTask,
    stationSelected,
    navigate,
    recipe_id,
    recipe,
    showNotification,
    validateTasks,
    addCookingHistory,
    kitchenDetail?.kitchenId,
    user?.me?.id,
  ]);

  // New optimized merge confirmation handler
  const handleMergeConfirm = useCallback(async () => {
    try {
      if (!prevKitchenTask) {
        setShowMergeConfirm(false);
        return;
      }

      const validatedCurrentTasks = validateTasks(tasks).map((task) => ({
        ...task,
        cookIn: stationSelected, // Ensure cookIn is set for current tasks
      }));

      // Ensure cookIn is set for all previous tasks
      const prevTasksWithCookIn = prevKitchenTask.tasks.map((task) => ({
        ...task,
        cookIn: prevKitchenTask.selectedStation || task.cookIn || "np1",
      }));

      // Combine tasks from both recipes
      const combinedTasks = [...prevTasksWithCookIn, ...validatedCurrentTasks];

      // First merge the tasks to resolve any conflicts
      const mergedTasks = mergeRecipes(combinedTasks);

      console.log("Merged tasks before code generation:", mergedTasks);

      // Then generate the code from the merged tasks
      const mergedCode = generateCode(mergedTasks);

      navigate(`/cooking-start/${recipe_id}`, {
        state: {
          tasks: mergedTasks,
          recipe: {
            ...data?.getRecipeActions,
            // Include combined recipe name info for better display
            merged_recipe_names: [
              prevKitchenTask.recipe?.recipe_name ||
                `Recipe ${prevKitchenTask.recipe?.id || "Previous"}`,
              data?.getRecipeActions?.recipe_name ||
                `Recipe ${recipe_id || "Current"}`,
            ],
          },
          code: mergedCode,
          cookingHistoryId: currentCookingHistoryId,
        },
      });

      // Clear the previous kitchen task from localStorage to prevent reuse
      // localStorage.removeItem("kitchenTask");
      setPrevKitchenTask(null);

      setShowMergeConfirm(false);
    } catch (error) {
      console.error("Error while merging recipes:", error);
      showNotification("error", error.message);
      setShowMergeConfirm(false);
    }
  }, [
    tasks,
    prevKitchenTask,
    navigate,
    recipe_id,
    data,
    showNotification,
    validateTasks,
    stationSelected,
    currentCookingHistoryId,
  ]);

  // Cancel handler
  const handleMergeCancel = useCallback(() => {
    setShowMergeConfirm(false);
  }, []);

  const handleCookLater = useCallback(() => {
    setOpenScheduleModal(true);
  }, []);

  const handleScheduleClose = useCallback(() => {
    setOpenScheduleModal(false);
  }, []);

  const debugButtonStyles = {
    position: "fixed",
    right: theme.spacing(2),
    bottom: theme.spacing(2),
    zIndex: 9999,
    display: "flex",
    gap: 1,
    flexDirection: "column",
  };

  const debugPanelStyles = {
    position: "fixed",
    right: 20,
    bottom: 100,
    zIndex: 9998,
    width: 300,
    maxHeight: "60vh",
    overflowY: "auto",
    p: 2,
    bgcolor: "background.paper",
    boxShadow: 3,
    borderRadius: 2,
  };

  // Add this function
  const handleShowCode = () => {
    try {
      const code = generateCode(tasks, stationSelected.toString());
      alert(`Generated Code:\n\n${code}`);
    } catch (err) {
      alert(`Code Generation Error: ${err.message}`);
    }
  };

  // Enhanced refresh function to reload inventory and update tasks
  const refreshInventory = useCallback(() => {
    console.log("Refreshing inventory data");

    // Clear any pending updates
    if (taskUpdateTimeoutRef.current) {
      clearTimeout(taskUpdateTimeoutRef.current);
    }

    // Function to perform the actual task update with fresh inventory data
    const updateTasksWithInventory = (freshInventoryData) => {
      console.log(
        "Updating tasks with fresh inventory data:",
        freshInventoryData?.length || 0,
        "items"
      );

      setTasks((prevTasks) => {
        // Create a new tasks array to force React to detect changes
        const tasksToUpdate = [...prevTasks];

        // Force validation with the fresh inventory data
        const validatedTasks = validateTasks(tasksToUpdate);

        // Make sure to check if ready to cook
        checkIfReadyToCook(validatedTasks);

        return validatedTasks;
      });
    };

    // Refetch inventory data with network-only policy to bypass cache
    if (refetchInventory && typeof refetchInventory === "function") {
      return refetchInventory({
        fetchPolicy: "network-only", // Force a network request to get fresh data
      })
        .then((result) => {
          console.log("Inventory data refetched successfully");

          // Extract the fresh inventory data
          const freshInventoryData = result?.data?.getBoxes || [];

          // Update tasks using the fresh inventory data
          updateTasksWithInventory(freshInventoryData);

          return result; // Return the result for chaining
        })
        .catch((err) => {
          console.error("Error refreshing inventory:", err);

          // Even if there's an error, try to update with current data
          updateTasksWithInventory(inventoryData);

          throw err; // Rethrow for error handling
        });
    } else {
      // If no refetch available, at least update with current data
      updateTasksWithInventory(inventoryData);
      return Promise.resolve(); // Return a resolved promise for consistency
    }
  }, [refetchInventory, validateTasks, checkIfReadyToCook, inventoryData]);

  // Pass this to the IngredientsPanel props
  const ingredientsPanelProps = useMemo(
    () => ({
      tasks,
      inventoryData,
      onManualEntry: handleManualEntry,
      onTaskSwap: setTasks,
      matchInventoryWithTasks,
      checkIfReadyToCook,
      boxes: inventoryData,
      loading: boxesLoading,
      stationSelected,
      refreshInventory, // Add the refresh function
    }),
    [
      tasks,
      inventoryData,
      handleManualEntry,
      matchInventoryWithTasks,
      boxesLoading,
      stationSelected,
      refreshInventory, // Add to dependencies
    ]
  );

  // Functions to handle previous recipe viewing
  const handleViewPrevRecipe = (isViewing) => {
    setIsViewingPrevRecipe(isViewing);
  };

  const handleContinueCooking = (kitchenTask) => {
    // Continue cooking previous recipe
    if (kitchenTask && kitchenTask.recipe && kitchenTask.tasks) {
      const prevRecipeId = kitchenTask.recipe.id;
      navigate(`/cooking-start/${prevRecipeId}`, {
        state: {
          tasks: kitchenTask.tasks,
          recipe: kitchenTask.recipe,
          code: generateCode(kitchenTask.tasks),
        },
      });
    } else {
      showNotification(
        "error",
        "Cannot continue cooking - recipe data is incomplete"
      );
    }
  };

  const handleClearPrevKitchenTask = () => {
    // Clear previous kitchen task
    localStorage.removeItem("kitchenTask");
    setPrevKitchenTask(null);
    showNotification("success", "Previous recipe removed");
  };

  const handleSetStationSelected = (station) => {
    setStationSelected(station);
    localStorage.setItem("stationSelected", station);
  };

  const handleCookNow = () => {
    if (isReadyToCook) {
      handleCook();
    } else {
      showNotification(
        "warning",
        "Please complete ingredient mapping before cooking"
      );
    }
  };

  const handleScheduleCook = () => {
    if (isReadyToCook) {
      setOpenScheduleModal(true);
    } else {
      showNotification(
        "warning",
        "Please complete ingredient mapping before scheduling"
      );
    }
  };

  const handleScheduleConfirm = (date) => {
    setScheduledDate(date);
    setOpenScheduleModal(false);

    // Here you would implement the scheduling logic
    try {
      // Make sure all tasks have the cookIn property set
      const tasksWithStation = tasks.map((task) => ({
        ...task,
        cookIn: stationSelected,
      }));

      // Store in local storage or server
      const scheduledRecipe = {
        recipe: data?.getRecipeActions,
        tasks: tasksWithStation,
        scheduledDate: date.toISOString(),
        stationSelected,
      };

      localStorage.setItem(
        `scheduled_recipe_${recipe_id}`,
        JSON.stringify(scheduledRecipe)
      );
      showNotification(
        "success",
        `Recipe scheduled for ${date.toLocaleString()}`
      );
    } catch (error) {
      console.error("Error scheduling recipe:", error);
      showNotification("error", "Failed to schedule recipe");
    }
  };

  if (loading) return <LoadingPage />;
  if (error) return <ErrorPage message="Recipe not found" />;

  return (
    <>
      {debugMode && (
        <DebugPanel
          show={showDebugPanel}
          onToggle={() => setShowDebugPanel(!showDebugPanel)}
          currentTasks={tasks}
          previousTasks={prevKitchenTask}
          stationSelected={stationSelected}
          generateCode={generateCode}
          generateMergedCode={generateMergedCode}
        />
      )}
      <BreadCrumb
        links={[
          { label: "Dashboard", href: "/dashboard" },
          { label: "Recipes", href: "/recipes" },
          {
            label: data?.getRecipeActions?.recipe_name || `Recipe ${recipe_id}`,
            href: `/recipe/${recipe_id}`,
          },
        ]}
        currentPage={"Cook Recipe"}
      />
      {preloader ? (
        <Preloader />
      ) : (
        <Box sx={{ ...mobileResponsiveStyles, mt: 2 }}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <RecipeDetailsPanel
                {...recipeDetailsProps}
                sx={{
                  height: "100%",
                  "@media (max-width: 600px)": {
                    marginBottom: "12px",
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  height: "100%",
                }}
              >
                {/* {prevKitchenTask && !isReadyToCook && ( */}
                <PreviousRecipeCard
                  prevRecipe={prevKitchenTask}
                  prevNutriPot={prevKitchenTask?.selectedStation}
                  isViewingPrevRecipe={isViewingPrevRecipe}
                  onViewPrevRecipe={() => handleViewPrevRecipe(true)}
                  onClosePrevRecipe={removePreKitechenTasks}
                  onMergeCooking={() => setShowMergeConfirm(true)}
                  sx={{
                    "@media (max-width: 600px)": {
                      padding: "12px",
                    },
                  }}
                />
                {/* )} */}

                <IngredientsPanel
                  tasks={tasks}
                  inventoryBoxes={inventoryData}
                  onManualEntry={handleManualEntry}
                  mappedPositions={mappedPositions}
                  setMappedPositions={setMappedPositions}
                  stationSelected={stationSelected}
                  setStationSelected={handleSetStationSelected}
                  checkIfReadyToCook={checkIfReadyToCook}
                  setIsReadyToCook={setIsReadyToCook}
                  isReadyToCook={isReadyToCook}
                  sx={{
                    flexGrow: 1,
                    "@media (max-width: 600px)": {
                      maxHeight: "none",
                    },
                  }}
                />
              </Box>
            </Grid>

            <Grid item xs={12}>
              <CookingControls
                recipe={recipe}
                isReady={isReadyToCook}
                onCookLater={handleScheduleCook}
                onCook={handleCookNow}
                hasPrevious={!!prevKitchenTask}
                onAddAnother={handleAddAnotherRecipe}
                mappedPositions={mappedPositions}
                noIngredientsRequired={!!isReadyToCook}
                sx={{
                  marginTop: theme.spacing(2),
                  padding: "16px",
                  borderRadius: "8px",
                  "@media (max-width: 600px)": {
                    padding: "12px",
                    marginBottom: "24px",
                  },
                }}
              />
            </Grid>
          </Grid>

          {/* Debug panel for development */}
          {debugMode && (
            <Fab
              color="secondary"
              aria-label="debug"
              onClick={() => setShowDebugPanel(!showDebugPanel)}
              sx={{
                position: "fixed",
                bottom: theme.spacing(4),
                right: theme.spacing(4),
                zIndex: 999,
              }}
            >
              <BugReport />
            </Fab>
          )}

          {/* Schedule cooking dialog */}
          <CookLaterDialog
            open={openScheduleModal}
            onClose={() => setOpenScheduleModal(false)}
            onSchedule={handleScheduleConfirm}
          />

          {/* Recipe Customizer Dialog */}
          <Dialog
            open={showRecipeCustomizer}
            onClose={handleCloseCustomizer}
            maxWidth="md"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
              sx: {
                borderRadius: isMobile ? 0 : 3,
                margin: isMobile ? 0 : 2,
                maxHeight: isMobile ? "100vh" : "90vh",
              },
            }}
          >
            <RecipeCustomizer
              tasks={tasks}
              recipe={recipe}
              originalQuantities={originalQuantities}
              onTasksUpdate={handleUpdateTasksFromCustomizer}
              onClose={handleCloseCustomizer}
            />
          </Dialog>

          {/* Merge with previous recipe confirmation */}
          <Dialog
            open={showMergeConfirm}
            onClose={() => setShowMergeConfirm(false)}
            PaperProps={{
              sx: {
                borderRadius: 2,
                width: "100%",
                maxWidth: { xs: "95%", sm: 500 },
              },
            }}
          >
            <DialogTitle sx={{ fontWeight: 600 }}>
              Merge with Previous Recipe?
            </DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ mb: 2 }}>
                Do you want to merge your current recipe{" "}
                <strong>{mergeInfo.currentRecipeName}</strong> on{" "}
                <strong>
                  {mergeInfo.currentNutriPot === "np1" ? "Pot 1" : "Pot 2"}
                </strong>{" "}
                with your previous recipe{" "}
                <strong>{mergeInfo.prevRecipeName}</strong> on{" "}
                <strong>
                  {mergeInfo.prevNutriPot === "np1" ? "Pot 1" : "Pot 2"}
                </strong>
                ?
              </DialogContentText>
              <DialogContentText color="warning.main">
                All progress from the previous recipe will be preserved, and you
                will continue cooking from where you left off.
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button
                onClick={() => setShowMergeConfirm(false)}
                color="primary"
                variant="outlined"
                fullWidth
                sx={{
                  minHeight: "44px",
                  "@media (max-width: 600px)": {
                    minHeight: "48px",
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleMergeConfirm}
                color="secondary"
                variant="contained"
                fullWidth
                sx={{
                  minHeight: "44px",
                  "@media (max-width: 600px)": {
                    minHeight: "48px",
                  },
                }}
              >
                Merge & Continue
              </Button>
            </DialogActions>
          </Dialog>

          {/* Debug panel */}
          <Dialog
            open={showDebugPanel}
            onClose={() => setShowDebugPanel(false)}
            fullScreen
          >
            <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
              <Code sx={{ mr: 1 }} />
              Debug Panel
              <IconButton
                onClick={() => setShowDebugPanel(false)}
                sx={{ ml: "auto" }}
              >
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <DebugPanel
                recipe={recipe}
                tasks={tasks}
                mappedPositions={mappedPositions}
                inventoryBoxes={inventoryData}
              />
            </DialogContent>
          </Dialog>
        </Box>
      )}
    </>
  );
});

export default CookRecipeNew;
